import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellRing } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from "./ui/hover-card";
import api, { formatPrice } from "../lib/api";

/**
 * Header-pill mit Hover-Preview der letzten Saved-Search-Treffer.
 *
 * Triggert beim ersten Open einen GET /saved-searches/recent-matches und
 * cached das Ergebnis 60 Sekunden, damit wiederholtes Hovern kein Spam
 * Richtung Backend erzeugt. Klick auf einen Treffer öffnet das jeweilige
 * Inserat; Klick auf den Pill selbst leitet auf den Saved-Searches-Tab.
 */
export default function SavedSearchPillPopover({ count }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const cacheRef = useRef({ ts: 0, payload: null });

    useEffect(() => {
        if (!open) return;
        const FRESH_MS = 60 * 1000;
        const now = Date.now();
        if (cacheRef.current.payload && now - cacheRef.current.ts < FRESH_MS) {
            setData(cacheRef.current.payload);
            return;
        }
        let cancelled = false;
        setLoading(true);
        api.get("/saved-searches/recent-matches", { params: { limit: 5 } })
            .then((res) => {
                if (cancelled) return;
                cacheRef.current = { ts: Date.now(), payload: res.data };
                setData(res.data);
            })
            .catch(() => {
                if (cancelled) return;
                setData({ items: [], total_searches: 0 });
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open]);

    const items = data?.items || [];
    const totalSearches = data?.total_searches || 0;

    return (
        <HoverCard openDelay={120} closeDelay={150} onOpenChange={setOpen}>
            <HoverCardTrigger asChild>
                <button
                    type="button"
                    onClick={() => navigate("/dashboard?tab=saved-searches")}
                    className="group relative inline-flex items-center gap-1.5 h-10 px-3 rounded-sm bg-white border-2 border-[#111827] text-[#111827] hover:bg-[#FFCC00] font-bold text-xs uppercase tracking-wider transition-colors"
                    data-testid="saved-search-pill"
                    title={t("saved_search_pill.tooltip", { count })}
                    aria-label={t("saved_search_pill.aria", { count })}
                >
                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 animate-ping" aria-hidden="true" />
                    <BellRing className="w-4 h-4" />
                    <span className="font-mono-num" data-testid="saved-search-count">{count}</span>
                    <span className="hidden xl:inline">{t("saved_search_pill.label")}</span>
                </button>
            </HoverCardTrigger>
            <HoverCardContent
                align="end"
                sideOffset={10}
                className="w-80 p-0 border-2 border-[#111827] rounded-sm shadow-xl"
                data-testid="saved-search-popover"
            >
                <div className="px-4 py-3 bg-[#111827] text-[#FFCC00]">
                    <div className="font-display text-sm font-black uppercase tracking-wider" data-testid="saved-search-popover-title">
                        {t("saved_search_pill.popover_title")}
                    </div>
                    <div className="text-[11px] text-white/70 font-semibold mt-0.5">
                        {t("saved_search_pill.popover_subtitle", { count, searches: totalSearches })}
                    </div>
                </div>

                <div className="bg-white" data-testid="saved-search-popover-body">
                    {loading && (
                        <div className="px-4 py-6 text-xs text-gray-500" data-testid="saved-search-popover-loading">
                            {t("saved_search_pill.popover_loading")}
                        </div>
                    )}
                    {!loading && items.length === 0 && (
                        <div className="px-4 py-6 text-xs text-gray-500" data-testid="saved-search-popover-empty">
                            {count > 0
                                ? t("saved_search_pill.popover_expired", { count })
                                : t("saved_search_pill.popover_empty")}
                        </div>
                    )}
                    {!loading && items.length > 0 && (
                        <ul className="divide-y divide-gray-100" data-testid="saved-search-popover-list">
                            {items.map((it) => (
                                <li key={it.id}>
                                    <button
                                        type="button"
                                        onClick={() => window.open(`/listing/${it.id}`, "_blank", "noopener,noreferrer")}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#FFFBEB] focus:bg-[#FFFBEB] focus:outline-none transition-colors"
                                        data-testid={`saved-search-popover-item-${it.id}`}
                                    >
                                        <div className="w-14 h-14 bg-gray-100 rounded-sm overflow-hidden flex-shrink-0">
                                            {it.thumb ? (
                                                <img
                                                    src={it.thumb}
                                                    alt=""
                                                    className="w-full h-full object-cover"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-400">
                                                    —
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-bold text-[#111827] truncate">
                                                {it.title || `${it.brand || ""} ${it.model || ""}`.trim() || "—"}
                                            </div>
                                            <div className="text-[11px] text-gray-500 truncate">
                                                {it.matched_search_name
                                                    ? t("saved_search_pill.popover_match_in", { name: it.matched_search_name })
                                                    : (it.city || "")}
                                            </div>
                                            <div className="text-[13px] font-black text-[#111827] font-mono-num mt-0.5">
                                                {formatPrice(it.price)}
                                            </div>
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="border-t border-gray-200 bg-[#F9FAFB] px-3 py-2">
                    <button
                        type="button"
                        onClick={() => navigate("/dashboard?tab=saved-searches")}
                        className="w-full text-center text-[11px] font-bold uppercase tracking-wider text-[#111827] hover:underline underline-offset-2"
                        data-testid="saved-search-popover-view-all"
                    >
                        {t("saved_search_pill.popover_view_all")} →
                    </button>
                </div>
            </HoverCardContent>
        </HoverCard>
    );
}
